import {
	Delete as DeleteIcon,
	Edit as EditIcon,
	StopCircle as StopIcon,
	ArrowBack as BackIcon,
} from "@mui/icons-material";
import {
	Button,
	ButtonGroup,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Stack,
	styled,
	Tab,
	Tooltip,
	Typography,
} from "@mui/material";
import { api } from "api";
import { UserListBasic, UserReport, UUID } from "api/types";
import { AssignmentChipStatus } from "components/AssignmentChipStatus";
import { AssignmentDetailDialog } from "components/AssignmentDetailDialog";
import { ColorTabs } from "components/ColorTabs";
import { InfoSnackBar } from "components/InfoSnackBar";
import { subMinutes } from "date-fns";
import { Assignment, getStatus } from "helpers/assignment";
import { formatDateForAPI, formatDateStr } from "helpers/date";
import { useAuthStatus } from "helpers/useAuthStatus";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { DELETE_ASSIGNMENT_CONFIRMATION } from "messages";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { routes } from "routes";
import { AssignmentDetailProgressTab } from "./AssignmentDetailProgressTab";
import { AssignmentDetailSummaryTab } from "./AssignmentDetailSummaryTab";
import { EmptyTab } from "./EmptyTab";

const PREFIX = "AssignmentDetailPage";

const classes = {
	tabPanel: `${PREFIX}-tabPanel`,
	assignmentName: `${PREFIX}-assignmentName`,
	chip: `${PREFIX}-chip`,
	tabs: `${PREFIX}-tabs`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.tabPanel}`]: {
		background: theme.palette.background.default,
	},

	[`& .${classes.assignmentName}`]: {
		marginTop: "unset",
		marginLeft: "unset",
	},

	[`& .${classes.chip}`]: {
		marginBottom: theme.spacing(1.5),
	},

	[`& .${classes.tabs}`]: {
		marginTop: theme.spacing(2),
	},
}));

interface TabPanelProps {
	children?: React.ReactNode;
	index: string;
	value: string;
}
export const AssignmentDetailPage = (): JSX.Element => {
	const { role } = useAuthStatus();
	const { updateLastBreadcrumbs } = useBreadcrumbs();
	const history = useHistory();
	const [tabVal, setTabVal] = useState("summary");
	const [assignment, setAssignment] = useState<Assignment>();
	const params = useParams<{ assignmentId: UUID }>();
	const [err, setErr] = useState(false);
	const [groupName, setGroupName] = useState("");
	const isMounted = useRef(true);
	const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
	const [isRecover, setIsRecover] = useState(false);

	const [openDel, setOpenDel] = useState(false);
	const [leaderBoard, setLeaderBoard] = useState<UserListBasic>([]);
	const [userReport, setUserReport] = useState<UserReport[] | null>();
	const fetchData = useCallback(() => {
		Promise.all([
			api.getAssignment(params.assignmentId),
			api.getAssignmentLeaderboard(params.assignmentId),
		])
			.then(async (res) => {
				if (!isMounted.current) {
					return;
				}

				const [assignmentRes, leaderboardRes, ..._] = res;
				if (assignmentRes.status !== 200 || leaderboardRes.status !== 200) {
					throw new Error();
				}

				const now = new Date();
				setAssignment({
					...assignmentRes.data,
					status: getStatus(assignmentRes.data, now),
				});
				updateLastBreadcrumbs({ name: assignmentRes.data.name, params });

				const LeaderBoard: UserListBasic = leaderboardRes.data;
				setLeaderBoard(LeaderBoard);

				if (
					assignmentRes.data.groupId.length !== null &&
					assignmentRes.data.groupId.length !== 0
				) {
					// Waiting means that the error will be thrown
					await onGetGroupName(assignmentRes.data.groupId);
				}
			})
			.catch(() => {
				if (!isMounted.current) {
					return;
				}

				setErr(true);
			});
	}, [params, updateLastBreadcrumbs]);

	useEffect(() => {
		if (!isMounted.current) {
			return;
		}

		fetchData();
		return () => {
			isMounted.current = false;
		};
	}, [fetchData]);

	const onGetGroupName = async (groupId: UUID): Promise<void> => {
		const res = await api.getGroup(groupId);
		if (res.status !== 200) {
			throw new Error();
		} else {
			setGroupName(res.data.name);
		}
	};

	const onDeleteAssignment = (): void => {
		if (assignment) {
			api
				.deleteAssignment(assignment.assignmentId)
				.then((res) => {
					if (res.status !== 204) {
						setErr(true);
						throw new Error();
					}
					history.push(routes._.assignments.path);
				})
				.catch(() => {
					setErr(true);
				});
		}
	};
	const handleTabChange = (
		event: React.ChangeEvent<{}>,
		newValue: string,
	): void => {
		setTabVal(newValue);
		if (newValue === "progress") {
			if (assignment && !userReport) {
				api
					.getAssignmentReport(assignment.assignmentId)
					.then((res) => {
						if (res.status !== 200) {
							throw new Error();
						}
						setUserReport(res.data);
						setTabVal(newValue);
					})
					.catch(() => setErr(true));
			} else {
				setTabVal(newValue);
			}
		}
	};

	const onEndAssignment = (): void => {
		if (assignment) {
			const now = formatDateForAPI(subMinutes(new Date(), 5).toISOString());
			api
				.updateAssignment(assignment.assignmentId, {
					...assignment,
					startDate: now,
					endDate: now,
				})
				.then((res) => {
					if (res.status !== 204) {
						setErr(true);
						throw new Error();
					}

					fetchData();
				})
				.catch(() => setErr(true));
		}
	};

	const TabPanel = (props: TabPanelProps): JSX.Element => {
		const { children, value, index, ...other } = props;

		return (
			<Root
				role="tabpanel"
				hidden={value !== index}
				id={`simple-tabpanel-${index}`}
				aria-labelledby={`simple-tab-${index}`}
				{...other}
				className={classes.tabPanel}
			>
				{value === index && children}
			</Root>
		);
	};

	const actionButtonGroup = (
		<Stack direction="row-reverse" alignItems="flex-start" marginTop={2}>
			<ButtonGroup
				variant="contained"
				color="secondary"
				aria-label="Assignment actions"
			>
				<Tooltip title="End assignment">
					<Button
						data-testid="end-asm-btn"
						onClick={(): void => onEndAssignment()}
						startIcon={<StopIcon />}
					>
						End
					</Button>
				</Tooltip>

				<Tooltip title="Recover assignment">
					<Button
						data-testid="recover-asm-btn"
						onClick={(): void => {
							setIsEditDialogOpen(true);
							setIsRecover(true);
						}}
						startIcon={<BackIcon />}
					>
						Recover
					</Button>
				</Tooltip>

				<Tooltip title="Delete assignment">
					<Button
						data-testid="del-asm-btn"
						onClick={(): void => setOpenDel(true)}
						startIcon={<DeleteIcon />}
					>
						Delete
					</Button>
				</Tooltip>

				<Tooltip title="Edit assignment">
					<Button
						data-testid="edit-asm-btn"
						onClick={() => setIsEditDialogOpen(true)}
						startIcon={<EditIcon />}
					>
						Edit
					</Button>
				</Tooltip>
			</ButtonGroup>
		</Stack>
	);

	return (
		<Root>
			<InfoSnackBar
				isOpen={err}
				severity="error"
				onClose={() => setErr(false)}
				message={"error"}
			/>
			<Grid container flexDirection="column">
				<Stack display="grid" gridTemplateColumns="auto 1fr">
					<Typography variant="h2" data-testid="asm-name">
						{assignment?.name}
					</Typography>
					{role !== "student" && actionButtonGroup}
				</Stack>

				<Typography variant="subtitle1">{groupName}</Typography>

				{assignment && (
					<Stack spacing={1} alignItems="flex-start">
						<AssignmentChipStatus
							size="small"
							label={assignment.status}
							data-testid="asm-chip"
						/>
						<Stack direction="row" flexWrap="wrap" width="100%" spacing={1}>
							<Chip size="small" label={formatDateStr(assignment.startDate)} />
							<p> to </p>
							<Chip size="small" label={formatDateStr(assignment.endDate)} />
						</Stack>
					</Stack>
				)}
			</Grid>

			<ColorTabs
				value={tabVal}
				onChange={handleTabChange}
				indicatorColor="secondary"
				className={classes.tabs}
			>
				<Tab value={"summary"} label="Summary" wrapped />
				<Tab
					value={"progress"}
					label="Progress"
					wrapped
					data-testid="progress-view"
				/>
			</ColorTabs>

			<TabPanel value={tabVal} index="summary">
				{assignment && (
					<AssignmentDetailSummaryTab
						assignment={assignment}
						ranking={leaderBoard}
					/>
				)}
			</TabPanel>

			<TabPanel value={tabVal} index="progress">
				{userReport ? (
					<AssignmentDetailProgressTab report={userReport} />
				) : (
					<EmptyTab name={`the assignment "${assignment?.name || ""}"`} />
				)}
			</TabPanel>

			{/* Edit dialog */}
			{assignment && (
				<AssignmentDetailDialog
					open={isEditDialogOpen}
					detail={assignment}
					handleClose={() => setIsEditDialogOpen(false)}
					mode={isRecover ? "recover" : "edit"}
					activities={assignment?.activities}
				/>
			)}

			{/* Delete dialog */}
			<Dialog open={openDel} onClose={(): void => setOpenDel(false)}>
				<DialogTitle>Are you sure?</DialogTitle>
				<DialogContent>
					<Typography variant="caption">
						{DELETE_ASSIGNMENT_CONFIRMATION}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenDel(false)}>Cancel</Button>
					<Button onClick={onDeleteAssignment}>Confirm</Button>
				</DialogActions>
			</Dialog>
		</Root>
	);
};
